import {Box, Heading, SimpleGrid} from '@chakra-ui/react';
import {ProductDetailsModal, ProviderProduct} from 'components/shared';
import {ProductSkeleton} from 'components/shared/skeletons/product-skeleton';
import React, {FC, useEffect} from 'react';
import {TranslationKeys} from 'translations';
import {ICartItem, IProduct, IRestaurantResponse} from 'types';
import {CrossSellingSections} from 'utils/provider';

interface IResultProducts {
  name: string;
  id: number;
  products: IProduct[];
}

interface IProps {
  sortedCategoriesWithProducts: IResultProducts[];
  cartItems: ICartItem[];
  locale?: string;
  isLoadingProducts: boolean;
  isMarket: boolean;
  isOpenProductModal: boolean;
  isDisabledProvider: boolean;
  isDisabledAddProduct?: boolean;
  t: (term: TranslationKeys) => string;
  crossSelling: CrossSellingSections[];
  selectedProduct?: IProduct;
  handleCloseModal: () => void;
  handleProductClick: (product: IProduct) => void;
  handleAddProduct: (cartItem: ICartItem, crosss?: ICartItem[]) => void;
  providerSlug?: string;
  provider?: IRestaurantResponse;
}

const skeletonArray = new Array(20).fill(1);

export const Products: FC<IProps> = ({
  sortedCategoriesWithProducts,
  cartItems,
  isLoadingProducts,
  locale,
  handleProductClick,
  isMarket,
  t,
  handleCloseModal,
  isOpenProductModal,
  selectedProduct,
  crossSelling,
  handleAddProduct,
  isDisabledAddProduct,
  providerSlug,
  provider,
}) => {
  useEffect(() => {
    if (sortedCategoriesWithProducts.length > 0) {
      const category = window.location.hash.substring(1);
      if (category) {
        const element = document.getElementById(category);
        element?.scrollIntoView({behavior: 'smooth'});
      }
    }
  }, [sortedCategoriesWithProducts]);

  return (
    <>
      {sortedCategoriesWithProducts.map((category) => {
        return (
          <Box
            as="section"
            id={`category-${category.id}`}
            key={category.id}
            mb="50px"
            mt="30px"
            _before={{
              content: '" "',
              display: 'block',
              top: 0,
              h: '190px',
              mt: '-180px',
              w: '100%',
            }}
          >
            <Heading as="h3" fontSize={['18px', '24px']} pb={['0px', '30px']} variant="section">
              {category.name}
            </Heading>
            <SimpleGrid
              columns={[2, 4]}
              spacing={['10px', '30px']}
              sx={{
                '@media (min-width: 768px) and (max-width: 1320px)': {
                  gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                },
              }}
            >
              {category?.products?.map((product) => {
                const is_selected = cartItems.filter((item) => item.product.id === product.id);
                return (
                  <ProviderProduct
                    key={product.id}
                    product={product}
                    onClick={() => handleProductClick(product)}
                    is_selected={!!is_selected.length}
                    locale={isMarket ? locale : undefined}
                    provider={provider}
                  />
                );
              })}
            </SimpleGrid>
          </Box>
        );
      })}
      {isLoadingProducts && (
        <SimpleGrid
          columns={[2, 4]}
          spacing={['10px', '30px']}
          mt="70px"
          sx={{
            '@media (min-width: 768px) and (max-width: 1320px)': {
              gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
            },
          }}
        >
          {skeletonArray.map((_, index) => (
            <ProductSkeleton key={index} />
          ))}
        </SimpleGrid>
      )}
      {isOpenProductModal && selectedProduct && (
        <ProductDetailsModal
          isOpen={isOpenProductModal}
          onClose={handleCloseModal}
          product={selectedProduct}
          onAdd={handleAddProduct}
          t={t}
          crossSelingProducts={crossSelling}
          isDisabledAddProduct={isDisabledAddProduct}
          providerSlug={providerSlug}
        />
      )}
    </>
  );
};
